import React, { useContext, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import Tooltip from 'components/Tooltip';
import { PortalsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import { buildReportShow } from 'navigation/appRoutes';

import {
  domainBadgeType,
  isDomainShipped,
  ISSUE_LABELS,
  severityFor,
} from './constants';
import AiReadyEditor from './AiReadyEditor';
import { AiReadyContext } from './AiReadyProvider';
import ReportCounts from './ReportCounts';
import ReportDetail from './ReportDetail';
import ReportIssuesModal from './ReportIssuesModal';

// One report in the agent's roster: what it feeds, and what the agent would
// find inside it.
//
// Filters, dimensions and metrics are listed in full — no MAX_VIEW_* caps. Those
// exist to fit the agent's token budget; a human auditing the roster has to see
// everything or the audit proves nothing.

const RowDiv = styled.div`
  border: 1px solid var(--color-border-secondary);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
`;

const DescriptionDiv = styled.div`
  margin-top: 4px;
  min-width: 0;
`;

const CountsDiv = styled.div`
  margin-top: 4px;
`;

const AiReadyReportRow = ({
  report,
  onAddTest,
}: {
  report: AiReady.AnalysedReport;
  // Opens a golden-question test for this report (or a metric line within it).
  onAddTest?: (params: {
    report: AiReady.AnalysedReport;
    metricId?: string;
  }) => void;
}) => {
  const { selectedPortal } = useContext(PortalsContext);
  const { shippedDomains, suggestions, coverage } = useContext(AiReadyContext);

  // Expansion lives in the URL, matching KnowledgeTab's `?section=`: a finding
  // about one report is then a link someone can paste, which is the whole point
  // of a screen people review together.
  const [searchParams, setSearchParams] = useSearchParams();
  const isExpanded = searchParams.get('report') === report.reportId;
  const toggleExpanded = () => {
    const next = new URLSearchParams(searchParams);
    if (isExpanded) {
      next.delete('report');
    } else {
      next.set('report', report.reportId);
    }
    setSearchParams(next, { replace: true });
  };
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isShowingIssues, setIsShowingIssues] = useState<boolean>(false);

  // Read off the suggestions rather than report.issues: redundancy is only
  // visible by comparing reports, so it is computed there and would be missing
  // from a per-report list.
  const findings = suggestions.filter(
    (suggestion) =>
      suggestion.reportId === report.reportId &&
      suggestion.kind !== 'makeAiReady',
  );
  const hasError = findings.some(
    (finding) => severityFor(finding.kind) === 'error',
  );
  // The hover has to name the findings, or the icon is just a number: a count
  // alone does not tell you whether it is worth opening.
  const findingsSummary = findings
    .map((finding) => ISSUE_LABELS[finding.kind])
    .join(', ');

  return (
    <RowDiv data-testid={`ai-ready-report-${report.reportId}`}>
      <Row centerAlign spaceBetween>
        <Row centerAlign style={{ gap: 8, flexWrap: 'wrap' }}>
          <Typography.Body type="Body 13 Bold">
            {report.reportName}
          </Typography.Body>
          {report.domains.length === 0 && (
            // Grey, not red: this fills the slot where the domains would be so
            // an empty list does not look like a still-loading one. The warning
            // icon beside it carries the severity — two alarms for one problem
            // is what the badges were doing wrong.
            <Badge badgeType="Default" text="No domains" />
          )}
          {report.domains.map((domain) => (
            <Badge
              key={domain}
              badgeType={domainBadgeType({
                isShippedByKb: isDomainShipped({ domain, shippedDomains }),
              })}
              text={domain}
            />
          ))}
          {findings.length > 0 && (
            // One warning icon and a count, not a badge per finding. Spelling
            // every finding out here pushed the domains off the line on a bad
            // report and still did not say what to do — the detail belongs in
            // the modal this opens.
            <Tooltip content={findingsSummary}>
              <Button
                type="Ghost - No Padding"
                size="Small"
                icon="warning"
                iconColor={
                  hasError
                    ? 'var(--color-badge-red-text)'
                    : 'var(--color-badge-orange-text)'
                }
                label={`${findings.length}`}
                testId={`ai-ready-fix-${report.reportId}`}
                onClick={() => setIsShowingIssues(true)}
              />
            </Tooltip>
          )}
        </Row>
        <Row centerAlign style={{ gap: 8 }}>
          {onAddTest && (
            <Button
              type="Ghost"
              size="Small"
              label="+ Add test"
              testId={`ai-ready-add-test-${report.reportId}`}
              onClick={() => onAddTest({ report })}
            />
          )}
          <Button
            type="Ghost"
            size="Small"
            label={isExpanded ? 'Hide detail' : 'Show detail'}
            onClick={toggleExpanded}
          />
          <Button
            type="Ghost"
            size="Small"
            label="Edit"
            onClick={() => setIsEditing((current) => !current)}
          />
          <Button
            type="Secondary"
            size="Small"
            label="Open report"
            onClick={() =>
              window.open(
                buildReportShow({
                  id: report.reportId,
                  portal: selectedPortal,
                }),
                '_blank',
              )
            }
          />
        </Row>
      </Row>

      {/* One line, ellipsised. A good agent description is two or three
          sentences, which is exactly long enough to push every other row off
          the screen — the full text is on hover and in the expanded detail.
          "Not described" is worded as an absence rather than as the finding's
          own label ("No agent description"), which the warning counter already
          reports. */}
      <DescriptionDiv>
        <Typography.Body
          type="Body 12"
          color="var(--color-text-secondary)"
          isEllipsis
        >
          {report.agentDescription || 'Not described'}
        </Typography.Body>
      </DescriptionDiv>
      <CountsDiv>
        <ReportCounts report={report} />
      </CountsDiv>

      {isEditing && (
        <AiReadyEditor
          reportId={report.reportId}
          reportName={report.reportName}
          initialDomains={report.domains}
          initialDescription={report.agentDescription}
          isCurrentlyAiReady
          onSaved={() => setIsEditing(false)}
        />
      )}

      {isExpanded && (
        <ReportDetail
          report={report}
          onAddTestForMetric={
            onAddTest
              ? (metric) => onAddTest({ report, metricId: metric.id })
              : undefined
          }
        />
      )}
      {isShowingIssues && (
        <ReportIssuesModal
          report={report}
          findings={findings}
          clusters={coverage.clusters}
          close={() => setIsShowingIssues(false)}
        />
      )}
    </RowDiv>
  );
};

export default AiReadyReportRow;
